import { ReactNode, useRef, useState, useCallback, useLayoutEffect } from 'react';
import { MainToolbar } from '../../features/main-toolbar';
import { TerminalPanel, useTerminalStore } from '../../features/terminal';
import { cn } from '../../lib/utils';
import { isBrowserMode, RestrictedFeatureCTADialog } from '../../features/browser-mode';
import { PersonalModeFooter, OfflineLicenseBanner, AuthDialog, useLicenseCache, useSubscriptionStore, selectAuthStale, refreshEntitlementToken, verifySubscription, verifyOutcomeToEffect, getAccessToken, recordAuthIntentSource } from '../../core/licensing';
import { toast } from '../../components/ui/toaster';
import { useAnalytics } from '../../core/analytics/useAnalytics';
import { useSidebarStore, MIN_SIDEBAR_WIDTH, MAX_SIDEBAR_WIDTH, DEFAULT_SIDEBAR_WIDTH } from '../../features/database-objects/store/sidebarStore';
import { CollapsedSidebar } from './CollapsedSidebar';
import { AppStatusBar } from './AppStatusBar';
import { CommandPalette } from '../../features/command-palette';
import { useAppMenuCommands } from '../menu/useAppMenuCommands';
import { useAppMenuConfigSync } from '../menu/useAppMenuConfigSync';
import { useSplitterDrag } from '../hooks/useSplitterDrag';
import {
  registerKeybindingTarget,
  type KeybindingHandlersRef,
} from '../../features/keybindings/dispatch/targetRegistry';
import { openVqeFromActiveTab } from '../../features/workspace/visual-query-editor/workspace/openVqeFromActiveTab';
import { isBetaFeatureEnabled } from '../../core/beta';

/** Keyboard resize step for the sidebar seam, in pixels (Shift = 4x). */
const SIDEBAR_KEY_STEP = 8;
const SIDEBAR_KEY_STEP_BIG = 32;

interface MainShellProps {
  sidebar?: ReactNode;
  children: ReactNode;
  className?: string;
}

// Exported for the keybinding anti-drift fixture; production registration uses it below.
// eslint-disable-next-line react-refresh/only-export-components
export const MAIN_SHELL_KEYBINDING_COMMAND_IDS = [
  'global.toggleTerminal',
  'global.openVisualQueryEditor',
] as const;

// eslint-disable-next-line react-refresh/only-export-components
export function registerMainShellKeybindingTarget(
  handlers: KeybindingHandlersRef,
): () => void {
  return registerKeybindingTarget({
    scope: 'global',
    handlers,
    canExecute: (commandId) => commandId !== 'global.openVisualQueryEditor' ||
      isBetaFeatureEnabled('visual-query-editor'),
  });
}

export function MainShell({ sidebar, children, className }: MainShellProps) {
  const toggleTerminalPanel = useTerminalStore((state) => state.togglePanel);
  const [showTerminalCTA, setShowTerminalCTA] = useState(false);
  const terminalHandlersRef = useRef<KeybindingHandlersRef>({ current: {} });
  terminalHandlersRef.current.current = {
    'global.toggleTerminal': () => {
      if (isBrowserMode()) {
        setShowTerminalCTA(true);
        return;
      }
      toggleTerminalPanel();
    },
    'global.openVisualQueryEditor': () => {
      openVqeFromActiveTab(undefined, { source: 'shortcut', toggle: true });
    },
  };
  useLayoutEffect(
    () => registerMainShellKeybindingTarget(terminalHandlersRef.current),
    [],
  );

  // Sidebar state
  const isCollapsed = useSidebarStore((s) => s.isCollapsed);
  const sidebarWidth = useSidebarStore((s) => s.width);
  const setSidebarWidth = useSidebarStore((s) => s.setWidth);
  const hasHydrated = useSidebarStore((s) => s.hasHydrated);

  // Sidebar resize handle (CH-13): pointer-capture + rAF drag, keyboard step,
  // double-click reset. The panel div's width is mutated directly during the
  // drag (see `onDrag`) and only committed to the store on drag end, matching
  // the pre-existing mouse-drag behaviour this replaces.
  const sidebarPanelRef = useRef<HTMLDivElement>(null);
  const sidebarHandleRef = useRef<HTMLDivElement>(null);
  const sidebarDragStartWidthRef = useRef(sidebarWidth);
  const sidebarSplitter = useSplitterDrag({
    axis: 'x',
    onDrag: (deltaPx) => {
      const el = sidebarPanelRef.current;
      if (!el) return;
      const newWidth = Math.max(
        MIN_SIDEBAR_WIDTH,
        Math.min(MAX_SIDEBAR_WIDTH, sidebarDragStartWidthRef.current + deltaPx)
      );
      el.style.width = `${newWidth}px`;
      // Fix round 1, Finding 1: the store commit (and the React re-render it
      // triggers) deliberately stays at drag-end to avoid a re-render-per-
      // frame storm, so `aria-valuenow` must be driven directly off this
      // in-flight value instead — otherwise it reports the stale pre-drag
      // width for the whole duration of a pointer drag. Written straight to
      // the DOM node (same pattern as `el.style.width` above), not via
      // React state, so it stays free of a per-frame re-render.
      const handle = sidebarHandleRef.current;
      if (handle) {
        const rounded = Math.round(newWidth);
        handle.setAttribute('aria-valuenow', String(rounded));
        handle.setAttribute('aria-valuetext', `${rounded} pixels`);
      }
    },
    onDragEnd: () => {
      const el = sidebarPanelRef.current;
      if (el) setSidebarWidth(el.getBoundingClientRect().width);
    },
    onReset: () => {
      setSidebarWidth(DEFAULT_SIDEBAR_WIDTH);
      track('feature:splitter_reset', { surface: 'sidebar' });
    },
    onKeyStep: (direction, big) => {
      const step = big ? SIDEBAR_KEY_STEP_BIG : SIDEBAR_KEY_STEP;
      setSidebarWidth(sidebarWidth + direction * step);
    },
  });

  // During hydration, show expanded sidebar to avoid rapid mount/unmount of CollapsedSidebar
  // Once hydrated, use the actual persisted state
  const shouldShowCollapsed = hasHydrated && isCollapsed;

  // Offline license cache state (only in desktop mode)
  const {
    showCacheWarning,
    daysRemaining,
    validateCache,
    clockRollbackDetected,
    rollbackHours,
  } = useLicenseCache();
  const { fetchSubscription } = useSubscriptionStore();
  const authStale = useSubscriptionStore(selectAuthStale);
  const { track } = useAnalytics();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);

  // Handler to verify subscription/trial online.
  // Surfaces the outcome instead of silently doing nothing: when the OIDC
  // session has lapsed it flags the session stale (which surfaces the re-logon
  // banner) and opens the re-sign-in dialog, rather than failing silently.
  const handleVerifySubscription = useCallback(async () => {
    track('ui:license_verify_clicked');
    const outcome = await verifySubscription({
      getAccessToken,
      fetchSubscription,
      refreshEntitlementToken,
      validateCache,
    });

    track('ui:license_verify_result', { outcome });

    const effect = verifyOutcomeToEffect(outcome);
    toast[effect.toast.kind]({ title: effect.toast.title, description: effect.toast.description });
    if (effect.markStale) {
      useSubscriptionStore.getState().markAuthStale(true);
      recordAuthIntentSource('reauth');
      setAuthDialogOpen(true);
    }
  }, [track, fetchSubscription, validateCache]);

  // Renderer keydown dispatch owns configurable shortcuts. The native menu
  // routes clicks and unconsumed-key fallbacks through the same command router.
  useAppMenuCommands();
  // Keep menu visibility and fallback accelerator labels aligned with the
  // renderer-owned effective keymap.
  useAppMenuConfigSync();

  return (
    <div className={cn("flex flex-col h-screen bg-background text-foreground", className)}>
      {/* Main toolbar at the top, spanning the full width */}
      <MainToolbar />

      {/* Main content area with sidebar and workspace */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - collapsed view shown when collapsed */}
        {sidebar && shouldShowCollapsed && <CollapsedSidebar />}

        {/* Sidebar - always mounted to keep DatabaseExplorer active for data loading */}
        {/* Hidden when collapsed, visible when expanded */}
        {sidebar && (
          <div
            ref={sidebarPanelRef}
            className={cn("relative h-full", shouldShowCollapsed && "hidden")}
            style={{
              width: `${sidebarWidth}px`,
              minWidth: `${MIN_SIDEBAR_WIDTH}px`,
              maxWidth: `${MAX_SIDEBAR_WIDTH}px`
            }}
          >
            {/* Sidebar content. CH-10: the explorer column sits one step off
                the content canvas so the two surfaces are distinguishable. */}
            <div className="w-full h-full bg-sidebar">
              {sidebar}
            </div>

            {/* Resizable handle / sidebar seam.
                CH-10 (GATE-1 finding): this used to PAINT its whole 6px grab
                zone with `bg-muted` — six times every other hairline in the
                app on dark, and nearly invisible on light. The grab zone stays
                6px (dragging a 1px target is miserable); only the paint
                shrinks. The seam itself is the 1px `before:` line pinned to the
                right edge, so the hit area and the visual are now independent.
                T5.8 (CH-13) adds: `setPointerCapture` + rAF drag (via
                `useSplitterDrag`, shared with the Scripts split and the
                vertical-tabs column seam), double-click reset, and
                `role="separator"` + keyboard resize. Hover and active-drag
                are pinned to the identical `--primary` tint (not just the
                same class list) so a Tailwind hover-vs-state-class
                specificity fight can't produce a visible mismatch. */}
            <div
              ref={sidebarHandleRef}
              className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize z-10 bg-transparent before:absolute before:inset-y-0 before:right-0 before:w-px before:bg-divider before:transition-colors before:content-[''] hover:before:bg-primary/60 data-[dragging=true]:before:bg-primary/60 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
              style={{
                touchAction: 'none',
                userSelect: 'none'
              }}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize sidebar"
              aria-valuenow={Math.round(sidebarWidth)}
              aria-valuemin={MIN_SIDEBAR_WIDTH}
              aria-valuemax={MAX_SIDEBAR_WIDTH}
              aria-valuetext={`${Math.round(sidebarWidth)} pixels`}
              tabIndex={0}
              data-dragging={sidebarSplitter.isDragging ? 'true' : undefined}
              data-testid="sidebar-resize-handle"
              onPointerDown={(e) => {
                sidebarDragStartWidthRef.current = sidebarWidth;
                sidebarSplitter.onPointerDown(e);
              }}
              onDoubleClick={sidebarSplitter.onDoubleClick}
              onKeyDown={sidebarSplitter.onKeyDown}
            />
          </div>
        )}

        {/* Main content area with terminal panel */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Workspace content area */}
          <div className="flex-1 min-h-0 overflow-hidden">
            {children}
          </div>

          {/* Terminal panel (bottom) - only in desktop mode */}
          {!isBrowserMode() && <TerminalPanel />}

          {/* Offline license / stale-session banner (desktop only). Also shown
              when the session is stale so a Pro user gets a clear re-logon
              prompt even while the offline cache is still valid. */}
          {!isBrowserMode() && (showCacheWarning || authStale) && (
            <OfflineLicenseBanner
              daysRemaining={daysRemaining}
              onVerify={handleVerifySubscription}
              clockRollbackDetected={clockRollbackDetected}
              rollbackHours={rollbackHours ?? undefined}
              sessionStale={authStale}
              onReSignIn={() => {
                track('ui:license_resignin_clicked');
                recordAuthIntentSource('reauth');
                setAuthDialogOpen(true);
              }}
            />
          )}
        </div>
      </div>

      {/* App status bar (the Transfers chip), its own row while it has something to show — never over the content. */}
      <AppStatusBar />

      {/* Re-sign-in dialog, opened from "Verify Now" (lapsed session) or the
          stale-session banner's Sign In button. Single shared instance. */}
      <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} />

      {/* Personal mode footer (bottom of window, full width) */}
      <PersonalModeFooter />

      <RestrictedFeatureCTADialog
        open={showTerminalCTA}
        feature="terminal"
        onClose={() => setShowTerminalCTA(false)}
      />

      {/* Command Palette */}
      <CommandPalette />
    </div>
  );
}
